/**
 * Client-side Markdown to HTML renderer
 * Feature: 069-markdown-edits-migration
 * Task: T026
 *
 * Renders markdown to sanitized HTML with plugins for:
 * - @-mention object references (@ph1a, @us0b, etc.)
 * - @-mention shape references (@1-@9)
 *
 * Security:
 * - DOMPurify sanitization prevents XSS attacks
 * - Whitelist approach for allowed tags and attributes
 */

import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import { isValidGlobalIdFormat } from '@206mp/shared/utils/globalObjectId';
import logger from './logger';

// Type definition for markdown-it-regexp callback
type RegexpCallback = (match: RegExpMatchArray, utils: { escape: (text: string) => string }) => string;

// Simple inline implementation of markdown-it-regexp functionality
// The npm package uses older module format that doesn't work well with our setup
function createRegexpPlugin(pattern: RegExp, callback: RegexpCallback) {
  return function(md: MarkdownIt) {
    // Add an inline rule to handle our patterns
    md.inline.ruler.push('custom_regexp_' + pattern.source, function(state, silent) {
      const pos = state.pos;
      const str = state.src.slice(pos);

      // Check if we match at this position
      const match = str.match(pattern);
      if (!match || match.index !== 0) {
        return false;
      }

      if (!silent) {
        // Create a simple escape function
        const utils = {
          escape: (text: string) => md.utils.escapeHtml(text)
        };

        // Get the HTML from the callback
        const html = callback(match, utils);

        // Create a token for the HTML
        const token = state.push('html_inline', '', 0);
        token.content = html;
      }

      // Move position past the match
      state.pos += match[0].length;
      return true;
    });
  };
}

/**
 * Create mention plugin for @-mention object references
 * Converts @ph1a, @us0b, etc. into <span> elements
 */
function createMentionPlugin() {
  return createRegexpPlugin(
    // Regex: @ followed by valid global ID pattern
    // Pattern: 2 lowercase letters + alternating digits and lowercase letters
    /@([a-z]{2}([0-9][a-z])+)/,

    function(match: RegExpMatchArray, utils: { escape: (text: string) => string }) {
      const fullMatch = match[0];    // e.g., "@ph1a"
      const objectId = match[1];     // e.g., "ph1a"

      // Validate format using shared utility (security check)
      if (!isValidGlobalIdFormat(objectId)) {
        logger.warn('Markdown', 'Invalid mention format, rendering as plain text', objectId);
        return utils.escape(fullMatch);
      }

      // Generate mention span with data attribute
      return `<span class="mention" data-object-id="${utils.escape(objectId)}">@${utils.escape(objectId)}</span>`;
    }
  );
}

/**
 * Create shape reference plugin for @1-@9 references
 * Converts @1, @2, ... @9 into <span> elements
 */
function createShapeReferencePlugin() {
  return createRegexpPlugin(
    // Regex: @ followed by a single digit 1-9
    // Negative lookahead ensures it's not followed by another digit
    /@([1-9])(?!\d)/,

    function(match: RegExpMatchArray, utils: { escape: (text: string) => string }) {
      const shapeNumber = match[1];  // e.g., "1"

      // Generate shape reference span with data attribute
      return `<span class="shape-reference" data-shape="${utils.escape(shapeNumber)}">@${utils.escape(shapeNumber)}</span>`;
    }
  );
}

// Configure DOMPurify with allowed tags and attributes
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    // Standard markdown elements
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'strong', 'em', 'del', 's',
    'blockquote', 'pre', 'code',
    'ul', 'ol', 'li',
    'a', 'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    // Custom elements for mentions
    'span',
  ],
  ALLOWED_ATTR: [
    // Link attributes
    'href', 'title', 'target', 'rel',
    // Image attributes
    'src', 'alt',
    // Custom attributes for mentions and shape references
    'class', 'data-object-id', 'data-shape',
  ],
  // Transform links to add security attributes
  ADD_ATTR: ['target', 'rel'],
};

/**
 * Client-side markdown renderer class
 * Mirrors the API's MarkdownService functionality
 */
export class MarkdownRenderer {
  private md: MarkdownIt;

  constructor() {
    this.md = new MarkdownIt({
      html: false,   // Disable raw HTML for security
      breaks: true,  // GitHub-style line breaks
      linkify: true, // Auto-convert URLs to links
      typographer: true, // Enable smart quotes
    });

    // Register plugins
    this.md.use(createMentionPlugin());
    this.md.use(createShapeReferencePlugin());

    logger.debug('Markdown', 'Client-side MarkdownRenderer initialized');
  }

  /**
   * Render markdown to sanitized HTML
   *
   * @param markdown - Raw markdown content
   * @returns Sanitized HTML string
   */
  render(markdown: string): string {
    if (!markdown) return '';

    try {
      // Render markdown to HTML
      const rawHtml = this.md.render(markdown);

      // Sanitize with DOMPurify
      const sanitizedHtml = this.sanitize(rawHtml);

      logger.debug('Markdown', 'Rendered markdown', {
        inputLength: markdown.length,
        outputLength: sanitizedHtml.length
      });

      return sanitizedHtml;
    } catch (error) {
      logger.error('Markdown', 'Failed to render markdown', error);
      // Return escaped text as fallback
      return this.escapeHtml(markdown);
    }
  }

  /**
   * Sanitize HTML to prevent XSS attacks
   */
  private sanitize(html: string): string {
    return DOMPurify.sanitize(html, DOMPURIFY_CONFIG);
  }

  /**
   * Escape HTML special characters (fallback)
   */
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

// Configure DOMPurify hook once on module load
// Custom hook to transform external links
DOMPurify.addHook('afterSanitizeAttributes', function(node) {
  if (node.tagName === 'A') {
    const href = node.getAttribute('href') || '';
    if (href.startsWith('http://') || href.startsWith('https://')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
});

// Singleton instance for convenience
let rendererInstance: MarkdownRenderer | null = null;

/**
 * Get the singleton markdown renderer instance
 */
export function getMarkdownRenderer(): MarkdownRenderer {
  if (!rendererInstance) {
    rendererInstance = new MarkdownRenderer();
  }
  return rendererInstance;
}

/**
 * Render markdown to sanitized HTML (convenience function)
 *
 * @param markdown - Raw markdown content
 * @returns Sanitized HTML string
 */
export function renderMarkdown(markdown: string): string {
  return getMarkdownRenderer().render(markdown);
}
